/**
 * The view-as session (S13, SA5) and the break-glass grant that rides on it (S6).
 *
 * One module-level store, persisted in `sessionStorage` (it dies with the tab, like the session it
 * mirrors). The API client reads it to decide whether a request carries `X-Saltseed-View-As`; React
 * reads it through `useViewAs`. Nothing here talks to the server: starting and ending a session are
 * explicit API calls made by the dialogs, which then {@link setViewAs} / {@link clearViewAs}.
 */

export const VIEW_AS_HEADER = 'X-Saltseed-View-As'

export interface BreakglassState {
  grantId: string
  zoneId: string
  expiresAt: string
}

export interface ViewAsState {
  accountId: string
  accountName: string
  impersonationId: string
  expiresAt: string
  /** The staff member's own tier, remembered because /api/me answers as the account while viewing. */
  staffTier: string | null
  breakglass: BreakglassState | null
}

const STORAGE_KEY = 'saltseed.view-as'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** Parses a stored value; anything malformed is no session at all. */
export function parseViewAs(raw: string | null): ViewAsState | null {
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return null
    const accountId = str(parsed['accountId'])
    const accountName = str(parsed['accountName'])
    const impersonationId = str(parsed['impersonationId'])
    const expiresAt = str(parsed['expiresAt'])
    if (!accountId || !accountName || !impersonationId || !expiresAt) return null
    let breakglass: BreakglassState | null = null
    const bg = parsed['breakglass']
    if (isRecord(bg)) {
      const grantId = str(bg['grantId'])
      const zoneId = str(bg['zoneId'])
      const bgExpires = str(bg['expiresAt'])
      if (grantId && zoneId && bgExpires) breakglass = { grantId, zoneId, expiresAt: bgExpires }
    }
    const staffTier = str(parsed['staffTier']) ?? null
    return { accountId, accountName, impersonationId, expiresAt, staffTier, breakglass }
  } catch {
    return null
  }
}

/** True while the session has not passed its expiry. */
export function isLive(expiresAt: string, now: number = Date.now()): boolean {
  const at = Date.parse(expiresAt)
  return Number.isFinite(at) && at > now
}

/**
 * The header the client adds, or undefined. Only GETs, never `/staff/` (the staff API is not a
 * console page and refuses the header), and only while the session is live.
 */
export function viewAsHeaderFor(
  method: string,
  path: string,
  state: ViewAsState | null,
  now: number = Date.now(),
): string | undefined {
  if (state === null || method.toUpperCase() !== 'GET') return undefined
  if (!isLive(state.expiresAt, now)) return undefined
  const normalised = path.startsWith('/') ? path : `/${path}`
  if (normalised === '/staff' || normalised.startsWith('/staff/') || normalised.startsWith('/staff?')) {
    return undefined
  }
  return state.accountId
}

/** Seconds left, floored at zero, for the countdown banners. */
export function secondsLeft(expiresAt: string, now: number = Date.now()): number {
  const at = Date.parse(expiresAt)
  return Number.isFinite(at) ? Math.max(0, Math.floor((at - now) / 1000)) : 0
}

/** `mm:ss`, or `h:mm:ss` from an hour up. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  const h = Math.floor(safe / 3600)
  const m = Math.floor((safe % 3600) / 60)
  const s = safe % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

/* --- the store -------------------------------------------------------------- */

function load(): ViewAsState | null {
  try {
    return parseViewAs(window.sessionStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

let current: ViewAsState | null = typeof window === 'undefined' ? null : load()
const listeners = new Set<() => void>()

function publish(next: ViewAsState | null): void {
  current = next
  try {
    if (next === null) window.sessionStorage.removeItem(STORAGE_KEY)
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* storage blocked: the session lives for this page load only */
  }
  listeners.forEach((listener) => {
    listener()
  })
}

export function getViewAs(): ViewAsState | null {
  return current
}

export function setViewAs(state: ViewAsState): void {
  publish(state)
}

export function clearViewAs(): void {
  publish(null)
}

export function setBreakglass(breakglass: BreakglassState | null): void {
  if (current !== null) publish({ ...current, breakglass })
}

export function subscribeViewAs(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
